/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *|
 *| IBM Cognos Products: Dashboard
 *|
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'dashboard-analytics/dataSources/services/DataSourcesService'
], (_, UpgradeBase, DataSourcesService) => {

	/**
	 * Upgrade the aggragation type of categorical data item for x-axis and y-axis slots for Scatter and Bubble charts.
	 **/
	class DataItemAggregationTypeUpgradeforScatterAndBubble extends UpgradeBase {

		constructor() {
			super();
			this.VERSION = 1025;
			this.startVersionUsingDefault = 1019;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			if (!spec || !spec.widgets || _.isEmpty(spec.widgets)) {
				return Promise.resolve(spec);
			}

			this.spec = spec;

			const dataSourcesClass  = this._DataSourcesServices || DataSourcesService;
			this.dataSourcesService =  new dataSourcesClass({
				features:{
					API: this.data.dashboardApi,
					Logger: this.data.logger
				}
			});
			this.dataSourcesService.initializeSourcesCollection(this.spec);
			return this._upgradeAggregationType(spec);
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		/**
		* In R stream, the x-axis and y-axis slots for Scatter and Bubble charts supports ordinal type only.
		* In Endor-R1 stream, both slots support any type.
		* When dropping the categorical attribute onto either x-axis or y-axis, we have enforced model's default aggregation type.
		* In Endor-R2 stream, we want to remove the enforcement.
		* The fix is to go through the specs from either Helios or Endor-R1 to set aggregate attribute to the default aggregation type
		* if data item's aggregate attribute has not been set yet.
		*
		* @param {object} spec - spec to perform upgrade on
		*
		* @return {Promise} Promise to be resolved when upgrade performed
		*/
		_upgradeAggregationType(spec){
			let promises = [];
			const useDefaultAggr = this.data && this.data.originalSpecVersion && this.data.originalSpecVersion >= this.startVersionUsingDefault;

			_.each(spec.widgets, widget => {
				let dataViews = widget.data && widget.data.dataViews;
				let slots = widget.slotmapping && widget.slotmapping.slots;
				let isLiveWidgetType = widget.type === 'live';
				let isScatterOrBubbleVisType = widget.visId === 'com.ibm.vis.ravescatter' || widget.visId === 'com.ibm.vis.ravebubble';
				let modelRef = dataViews && dataViews[0] ? dataViews[0].modelRef : null;
				if (modelRef && dataViews && slots && isLiveWidgetType && isScatterOrBubbleVisType) {
					let xyAxisSlotsDataItems = [];
					_.each(slots, slot => {
						// get data items in the x-axis or y-axis slots
						if (slot.name === 'x' || slot.name === 'y') {
							xyAxisSlotsDataItems = xyAxisSlotsDataItems.concat(slot.dataItems);
						}
					});
					// if any data items start upgrading
					if (xyAxisSlotsDataItems.length > 0) {
						// get data module with dataset service
						let promise = this._getModule(modelRef).then(function(module){
							_.each(dataViews, dataView => {
								let dataItems = dataView.dataItems;
								if (dataItems){
									_.each(dataItems, dataItem =>{
										if (_.contains(xyAxisSlotsDataItems, dataItem.id) && !dataItem.aggregate){
											// get metadata with column id
											const mdc = module.getMetadataColumn(dataItem.itemId);
											if (mdc) {
												const type = mdc.getType();
												if (type === 'attribute') {
													// As of Endor the default aggregation type is changed to countdistinct to follow what is returned from modeling. 
													// Upgrade should keep 'count' in data item spec to retain the old behavior.
													dataItem.aggregate = useDefaultAggr ? mdc.getDefaultAggregation() : 'count';
												}
											}
										}
									});
								}
							});
						});
						promises.push(promise);
					}
				}
			});
			return Promise.all(promises).then(function(){ return spec; });
		}

		_getModule(modelRef) {
			return this.dataSourcesService.getModule(modelRef);
		}

	}

	return new DataItemAggregationTypeUpgradeforScatterAndBubble();
});
